import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import ConfigurationLocalAccountingBillableRateGridCopyModal from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-grid-copy-modal/configuration-local-accounting-billable-rate-grid-copy-modal.vue'
import ConfigurationLocalAccountingBillableRateGridCopyModalComp from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-grid-copy-modal/configuration-local-accounting-billable-rate-grid-copy-modal'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BillableRateBillableConceptResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.dto'
import { ConfigurationLocalAccountingRatesGridSidepanelComboElements, ConfigurationLocalAccountingRatesGridState, initialConfigurationLocalAccountingRatesGridState } from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-grid/configuration-local-accounting-billable-rate-grid.model'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent } from '@ag-grid-enterprise/all-modules'
import { BillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/configuration-local-accounting-billable-rates/billable-rate.dto'
import { StrictNullable } from '@/shared/types/app.types'
import ConfigurationLocalAccountingBillableRateModal from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-modal/configuration-local-accounting-billable-rate-modal.vue'
import ConfigurationLocalAccountingBillableRateModalComp from '@/modules/configurationModule/detail/local-accounting/billable/rates/components/rates-modal/configuration-local-accounting-billable-rate-modal'
import ConfigurationListSidepanel from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'
import ConfigurationListSidepanelComp from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'
import { ConfigurationListSidePanelEntity } from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.model'
import { billableRateBillableConceptService } from '@/modules/configurationModule/detail/local-accounting/billable/rates/services/billable-rate-billable-concept/billable-rate-billable-concept.service'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { CommonParams } from '@/api/resources/common/shared/models/common.models'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { configurationLocalAccountingBillableCustomerRatesService } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-billable-rates-services/configuration-local-accounting-billable-clients-rates.service'
import { ConfigurationLocalAccountingBillableCustomerBillableRateResponse } from '@/modules/configurationModule/detail/local-accounting/billable/clients/services/customer-billable-rates-services/configuration-local-accounting-billable-clients-rates.dto'
import { GridUtils } from '@/shared/util/grid-utils'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        GridDisplayOptions,
        ConfigurationLocalAccountingBillableRateModal,
        ConfigurationLocalAccountingBillableRateGridCopyModal,
        ConfigurationListSidepanel
    }
})
export default class ConfigurationLocalAccountingBillableRateGrid extends BaseCrudServerSideComponent<BillableRateResponse> implements BaseCrud {
    @Ref() readonly ratesModalWrapper!: typeof ConfigurationLocalAccountingBillableRateModalComp
    @Ref() readonly ratesCopyModalWrapper!: typeof ConfigurationLocalAccountingBillableRateGridCopyModalComp
    @Ref() readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: ConfigurationLocalAccountingRatesGridState

    constructor() {
        super()
        this.state = initialConfigurationLocalAccountingRatesGridState()
    }

    get canUpdateOrCopy(): boolean {
        return this.state.selectedRows.length === 1
    }

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                this.configurationListSidepanel.hide()
                if (await this._deletelocalAccountingBillableRates(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///
    onUpdate(localAccountingBillableRate: BillableRateResponse = this.state.selectedRows[0]): void {
        this.configurationListSidepanel.hide()
        if (localAccountingBillableRate) {
            this.showModal(this.$tc('configuration.actions.update'), localAccountingBillableRate, this.ratesModalWrapper)
        }
    }

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(localAccountingBillableRate => localAccountingBillableRate.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.configurationListSidepanel.hide()
        this.showModal(this.$tc('configuration.actions.add'), null, this.ratesModalWrapper)
    }

    onCopy(localAccountingBillableRate: BillableRateBillableConceptResponse = this.state.gridApi?.getSelectedRows()[0]): void {
        this.configurationListSidepanel.hide()
        this.showModal(this.$tc('configuration.actions.copy'), localAccountingBillableRate, this.ratesCopyModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.update'),
                cssClasses: !this.canUpdateOrCopy ? ['not-show'] : [''],
                action: () => this.onUpdate(params?.node?.data)
            },
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: !this.canDelete ? ['not-show'] : [''],
                action: this.onDelete
            },
            {
                name: this.$tc('configuration.actions.copy'),
                cssClasses: !this.canUpdateOrCopy ? ['not-show'] : [''],
                action: this.onCopy
            }
        ]
    }

    onRowClicked(event: RowClickedEvent): void {
        const rateSelected: BillableRateResponse = event.node.data
        this.configurationListSidepanel.show({
            title: rateSelected.name,
            entities: [
                {
                    id: ConfigurationLocalAccountingRatesGridSidepanelComboElements.CLIENTS,
                    entityKey: 'configuration.sections.client'
                },
                {
                    id: ConfigurationLocalAccountingRatesGridSidepanelComboElements.CONCEPTS,
                    entityKey: 'configuration.sections.concept'
                }
            ],
            obtainEntityList: async (entity: ConfigurationListSidePanelEntity): Promise<string[]> => {
                switch (entity.id as ConfigurationLocalAccountingRatesGridSidepanelComboElements) {
                case ConfigurationLocalAccountingRatesGridSidepanelComboElements.CLIENTS:
                    return this._getAssignedClients(rateSelected)
                case ConfigurationLocalAccountingRatesGridSidepanelComboElements.CONCEPTS:
                    return this._getAssignedConcepts(rateSelected)
                }
            }
        })
    }

    private async _getAssignedConcepts(rateSelected: BillableRateResponse): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'rate.id': GridUtils.getNumberFilterModel('equals', rateSelected.id),
            'active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }

        const laboratoryId = this.$store.getters.labId
        const response: StrictNullable<IResponseAgGrid<BillableRateBillableConceptResponse>> = await this.$_toCallSecure(
            billableRateBillableConceptService.paginator(
                filterModel,
                params,
                laboratoryId
            )
        )
        return response?.content.map((item: BillableRateBillableConceptResponse) => item.concept.name) || []
    }

    private async _getAssignedClients(rateSelected: BillableRateResponse): Promise<string[]> {
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        const filterModel = {
            'rate.id': GridUtils.getNumberFilterModel('equals', rateSelected.id),
            'customer.active': {
                filterType: 'boolean',
                type: 'equals',
                filter: true
            }
        }

        const laboratoryId = this.$store.getters.labId
        const response: StrictNullable<IResponseAgGrid<ConfigurationLocalAccountingBillableCustomerBillableRateResponse>> = await this.$_toCallSecure(
            configurationLocalAccountingBillableCustomerRatesService.paginator(
                filterModel,
                params,
                laboratoryId
            )
        )
        return response?.content.map((item: ConfigurationLocalAccountingBillableCustomerBillableRateResponse) => item.customer.name) || []
    }

    private async _deletelocalAccountingBillableRates(requestDelete: EntityIdentifiersRequest): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///
    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }
}
